import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  RefreshCw,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import type { CalendarShellSnapshot, CalendarViewMode } from "../core/shell-types";
import { rangeTitle, VIEW_MODES, viewModeLabel } from "./format";

const COMPACT_MODES: readonly CalendarViewMode[] = ["day", "agenda", "month"];

function IconButton({
  label,
  onClick,
  icon: Icon,
  compact,
  spinning,
  active,
}: {
  label: string;
  onClick: () => void;
  icon: typeof ChevronLeft;
  compact: boolean;
  spinning?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md text-foreground/80 transition-colors hover:bg-accent hover:text-foreground",
        compact ? "h-11 w-11" : "h-8 w-8",
        active && "bg-accent text-foreground",
      )}
    >
      <Icon aria-hidden className={cn("h-4 w-4", spinning && "animate-spin")} />
    </button>
  );
}

function ViewSwitcher({
  value,
  onChange,
  compact,
}: {
  value: CalendarViewMode;
  onChange: (mode: CalendarViewMode) => void;
  compact: boolean;
}) {
  const modes = compact ? COMPACT_MODES : VIEW_MODES;
  const overflow = compact ? VIEW_MODES.filter((mode) => !COMPACT_MODES.includes(mode)) : [];
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex items-center gap-1">
      <div
        role="toolbar"
        aria-label="Calendar view"
        className={cn(
          "flex items-center rounded-full border border-border/70 bg-muted/40 p-0.5",
          compact && "flex-1",
        )}
      >
        {modes.map((mode) => {
          const selected = mode === value;
          return (
            <button
              key={mode}
              type="button"
              aria-pressed={selected}
              data-view-mode={mode}
              onClick={() => onChange(mode)}
              title={`${viewModeLabel(mode)} view (${mode.slice(0, 1)})`}
              className={cn(
                "rounded-full text-sm transition-colors",
                compact ? "min-h-10 flex-1 px-3" : "px-3 py-1",
                selected
                  ? "bg-background font-medium text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {viewModeLabel(mode)}
            </button>
          );
        })}
      </div>
      {overflow.length > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label="More views"
            title="More views"
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-foreground/80 transition-colors hover:bg-accent",
              overflow.includes(value) && "bg-accent text-foreground",
            )}
          >
            <MoreHorizontal aria-hidden className="h-4 w-4" />
          </button>
          {open ? (
            <div
              role="menu"
              className="absolute right-0 top-full z-40 mt-1 w-40 rounded-lg border border-border bg-background p-1 shadow-xl"
            >
              {overflow.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="menuitemradio"
                  aria-checked={mode === value}
                  data-view-mode={mode}
                  onClick={() => {
                    setOpen(false);
                    onChange(mode);
                  }}
                  className={cn(
                    "flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm transition-colors hover:bg-accent",
                    mode === value && "font-medium",
                  )}
                >
                  {viewModeLabel(mode)}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export function CalendarToolbar({
  snapshot,
  compact,
  calendarsOpen,
  onToday,
  onNavigate,
  onSetView,
  onRefresh,
  onToggleCalendars,
}: {
  snapshot: CalendarShellSnapshot;
  compact: boolean;
  calendarsOpen: boolean;
  onToday: () => void;
  onNavigate: (delta: number) => void;
  onSetView: (mode: CalendarViewMode) => void;
  onRefresh: () => void;
  onToggleCalendars: () => void;
}) {
  const title = rangeTitle(snapshot, undefined, { compact });
  const isToday = rangeContainsToday(snapshot);

  return (
    <div
      className={cn(
        "flex flex-col gap-2 border-b border-border/70 px-3 py-2",
        !compact && "sm:flex-row sm:items-center sm:gap-3 sm:px-4",
      )}
    >
      <div className="flex min-w-0 items-center gap-1">
        <button
          type="button"
          onClick={onToday}
          title="Today (t)"
          className={cn(
            "shrink-0 rounded-full border border-border/70 px-3 text-sm transition-colors hover:bg-accent",
            compact ? "min-h-11" : "py-1",
            isToday && "border-primary/40 text-primary",
          )}
        >
          Today
        </button>
        <IconButton
          label="Previous"
          icon={ChevronLeft}
          onClick={() => onNavigate(-1)}
          compact={compact}
        />
        <IconButton
          label="Next"
          icon={ChevronRight}
          onClick={() => onNavigate(1)}
          compact={compact}
        />
        <h2
          className={cn(
            "min-w-0 flex-1 truncate font-display tracking-tight",
            compact ? "text-lg" : "text-xl",
          )}
          data-calendar-title
          aria-live="polite"
        >
          {title}
        </h2>
        {snapshot.isLoading ? (
          <output
            aria-label="Loading"
            className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
          >
            <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
            {!compact ? "Loading…" : null}
          </output>
        ) : null}
        {compact ? (
          <>
            <IconButton
              label="Refresh"
              icon={RefreshCw}
              onClick={onRefresh}
              compact
              spinning={snapshot.isLoading}
            />
            <IconButton
              label="Calendars"
              icon={CalendarDays}
              onClick={onToggleCalendars}
              compact
              active={calendarsOpen}
            />
          </>
        ) : null}
      </div>
      <div className={cn("flex items-center gap-1", !compact && "sm:ml-auto")}>
        <ViewSwitcher value={snapshot.viewMode} onChange={onSetView} compact={compact} />
        {!compact ? (
          <>
            <IconButton
              label="Refresh"
              icon={RefreshCw}
              onClick={onRefresh}
              compact={false}
              spinning={snapshot.isLoading}
            />
            <IconButton
              label={calendarsOpen ? "Hide calendars" : "Show calendars"}
              icon={CalendarDays}
              onClick={onToggleCalendars}
              compact={false}
              active={calendarsOpen}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

function rangeContainsToday(snapshot: CalendarShellSnapshot): boolean {
  const { start, end } = snapshot.range;
  const today = snapshot.today;
  const key = (d: { year: number; month: number; day: number }) =>
    d.year * 10000 + d.month * 100 + d.day;
  return key(start) <= key(today) && key(today) <= key(end);
}
